import { useEffect, useMemo, useState } from 'react';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import SingleArrowDown from '@r1-frontend/ui-react/components/svg/arrows/SingleArrowDown';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { getPluralize } from '@r1-frontend/shared/helpers/pluralize';
import { channelPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

import Subscriptions from '~/src/components/TariffCard/subscriptions';
import { ISubscriptionBody } from '~/src/components/TariffCard/subscriptions/types';
import { IOfferContents } from '~/src/entities/formattedTariff';

import * as ST from './styled';

const channelText = getPluralize(channelPluralize);

interface IActiveCardWrapper {
    children: React.ReactNode,
    isActive: boolean,
    onClick?: () => void,
}

export const ActiveCardWrapper = ({ children, isActive = false, onClick }: IActiveCardWrapper) => {
    return (
        <ST.ActiveCardWrapper isActive={isActive} onClick={onClick}>
            {children}
        </ST.ActiveCardWrapper>
    );
};

export const ArrowPointer = ({ isActive = false }: { isActive: boolean }) => (
    <ST.ArrowPositioner isActive={isActive}>
        <SingleArrowDown/>
    </ST.ArrowPositioner>
);

interface ITariffCardMinimalProps {
    title: string,
    price: number,
    speed: string,
    channelCount?: number,
    priceAction?: number | null,
    promoText?: string,
    bgColor: string | undefined,
    colorText?: string | null,
    imageBackground?: string | null,
    titleColors?: string[],
    isLightCard?: boolean,
    subscriptions: Array<IOfferContents>,
}

export const TariffCardMinimal = ({
    title,
    speed,
    priceAction,
    price,
    promoText,
    channelCount,
    bgColor = '',
    colorText = '',
    imageBackground = '',
    titleColors = [],
    isLightCard,
    subscriptions,
 }: ITariffCardMinimalProps): JSX.Element => {
    const isMobile = useMatchMedia(`(max-width: ${wideBreakpoints.mobile})`);
    const [cardWidth, setCardWidth] = useState(148);

    const preparedSubscriptions = useMemo<ISubscriptionBody[]>(() => {
        return subscriptions.map(item => ({
            id: item.id,
            image: item.image,
            imageWebp: item.image_webp,
            width: item.width,
            alt: item.title,
            type: 'subscription',
            sort: item.sort,
        }));
    }, [subscriptions]);

    useEffect(() => {
        setCardWidth(isMobile ? 100 : 148);
    }, [isMobile]);

    const hasBg = !!imageBackground;

    const cardBg = isLightCard ? COLORS.BgMain : COLORS.BgSurface;
    const descBg = isLightCard ? COLORS.BgSurface : COLORS.BgMain;

    return (
        <ST.TariffCardMinimal bg={hasBg ? `url(${imageBackground})` : cardBg} colorText={colorText || ''}>
            {promoText &&
                <ST.PromoText
                    colors={titleColors}
                    data-test='title'
                    hasBg={hasBg}
                >
                    {promoText}
                </ST.PromoText>
            }
            <ST.TariffTitle
                colors={titleColors}
                data-test='title'
                isSmall={!!promoText}
                hasBg={hasBg}
            >
                {promoText ? `Тариф «${title}»` : title}
            </ST.TariffTitle>
            <ST.TariffDescription
                data-test='description'
                hasBg={hasBg}
                defaultBg={descBg}
                colorText={colorText || ''}
                bgColor={bgColor}
            >
                <ST.PropsBlock>
                    <ST.DescriptionRow>{speed} Мбит/с</ST.DescriptionRow>
                    {!!channelCount && <ST.DescriptionRow>{`${channelCount} ТВ-${channelText(channelCount)}`}</ST.DescriptionRow>}
                </ST.PropsBlock>
                <Subscriptions
                    className={'customSubscriptions'}
                    items={preparedSubscriptions}
                    imageHeight={20}
                    hasBorderRadius={false}
                    cardInnerWidth={cardWidth}
                    parentWithOneBtn={cardWidth - 8}
                    parentWithTwoBtn={cardWidth - 16}
                    buttonWidth={10}
                />
                <ST.PriceBlock>
                    <ST.Price>{priceAction || price} ₽/мес</ST.Price>
                    {!!priceAction && <ST.OldPrice hasBg={hasBg}>{price} ₽/мес</ST.OldPrice>}
                </ST.PriceBlock>
            </ST.TariffDescription>
        </ST.TariffCardMinimal>
    );
};
